import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslate } from '@federico.moretti/react-translate';
import { perCentToString } from '../../utils';
import Text from '../Text';

const Container = styled.output`
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: baseline;
  line-height: 1.15;
`;

function NumberLabel({
  id,
  value,
  size = 'base',
  type,
  currency,
  isNegative,
  style = {},
  testid = 'number-label',
}) {
  const { t, language } = useTranslate();

  const sizes = {
    small: [14, 14],
    base: [22, 14],
    big: [28, 18],
  };

  if (value == null || Number.isNaN(Number(value))) {
    return (
      <Text
        id={id}
        testid={testid}
        noMargin
        inline
        style={{ lineHeight: 1.15, fontSize: sizes[size][0], ...style }}
        output
      >
        nd
      </Text>
    );
  }

  if (type === 'percentage') {
    const percent = perCentToString(value);
    const int = percent.slice(0, -4);
    const decimal = percent.slice(-4);

    return (
      <Container id={id} aria-label={percent} style={style}>
        <Text
          testid={testid}
          noMargin
          inline
          style={{ lineHeight: 1.15, fontSize: sizes[size][0], color: style.color }}
        >
          {isNegative ? `-${int}` : int}
        </Text>
        <Text
          testid={`${testid}-decimal`}
          noMargin
          inline
          style={{ lineHeight: 1.15, fontSize: sizes[size][1], color: style.color }}
        >
          {decimal}
        </Text>
      </Container>
    );
  }

  if (type === 'day' || type === 'year' || type === 'month') {
    const unit =
      type === 'year'
        ? t('years').toLowerCase()
        : type === 'month'
        ? t('months').toLowerCase()
        : t('days').toLowerCase();

    const label = `${value} ${unit}`;
    return (
      <Container id={id} aria-label={label} style={style}>
        <Text
          testid={testid}
          noMargin
          inline
          style={{ lineHeight: 1.15, fontSize: sizes[size][0], color: style.color }}
        >
          {value}
        </Text>
        <Text
          noMargin
          inline
          style={{ lineHeight: 1.15, fontSize: sizes[size][1], marginLeft: 3, color: style.color }}
        >
          {unit}
        </Text>
      </Container>
    );
  }

  const fixedValue = Number.parseFloat(value).toLocaleString(language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (type === 'currency') {
    return (
      <Container id={id} aria-label={fixedValue + currency} style={style}>
        <Text
          testid={testid}
          noMargin
          inline
          style={{ lineHeight: 1.15, fontSize: sizes[size][0], color: style.color }}
        >
          {fixedValue.slice(0, -3)}
        </Text>
        <Text
          testid={`${testid}-decimal`}
          noMargin
          inline
          style={{ lineHeight: 1.15, fontSize: sizes[size][1], color: style.color }}
        >
          {`${fixedValue.slice(-3)}${currency}`}
        </Text>
      </Container>
    );
  }

  if (Number.isInteger(value)) {
    return (
      <Text
        id={id}
        testid={testid}
        noMargin
        inline
        output
        style={{ lineHeight: 1.15, fontSize: sizes[size][0] }}
      >
        {value}
      </Text>
    );
  }

  return (
    <Container id={id} aria-labelledby={id} aria-label={fixedValue} style={style}>
      <Text
        testid={testid}
        noMargin
        inline
        style={{ lineHeight: 1.15, fontSize: sizes[size][0], ...style }}
      >
        {fixedValue.slice(0, -3)}
      </Text>
      <Text
        testid={`${testid}-decimal`}
        noMargin
        inline
        style={{ lineHeight: 1.15, fontSize: sizes[size][1] }}
      >
        {fixedValue.slice(-3)}
      </Text>
    </Container>
  );
}

NumberLabel.propTypes = {
  id: PropTypes.string,
  testid: PropTypes.string,
  value: PropTypes.number,
  type: PropTypes.oneOf(['percentage', 'day', 'month', 'year', 'currency']),
  currency: PropTypes.string,
  size: PropTypes.oneOf(['small', 'base', 'big']),
  isNegative: PropTypes.bool,
  style: PropTypes.object,
};

export default React.memo(NumberLabel);
